import { useEffect, useState } from "react";
import { Plus, Trash2, Save, Users, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { toast } from "sonner";

const empty = { name: "", emails: [], insurance_types: [] };

function TeamCard({ team, onSave, onDelete }) {
  const [draft, setDraft] = useState({
    name: team.name || "",
    emails: team.emails || [],
    insurance_types: team.insurance_types || [],
    _emailInput: "",
  });
  const [saving, setSaving] = useState(false);

  const toggleType = (t) => {
    setDraft((d) => ({
      ...d,
      insurance_types: d.insurance_types.includes(t)
        ? d.insurance_types.filter((x) => x !== t)
        : [...d.insurance_types, t],
    }));
  };

  const addEmail = () => {
    const v = (draft._emailInput || "").trim().toLowerCase();
    if (!v) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      toast.error("Invalid email");
      return;
    }
    if (draft.emails.includes(v)) {
      toast.error("Already added");
      return;
    }
    setDraft((d) => ({ ...d, emails: [...d.emails, v], _emailInput: "" }));
  };

  const removeEmail = (e) => {
    setDraft((d) => ({ ...d, emails: d.emails.filter((x) => x !== e) }));
  };

  const save = async () => {
    if (!draft.name.trim()) return toast.error("Team name is required");
    if (draft.emails.length === 0) return toast.error("Add at least one email");
    if (draft.insurance_types.length === 0) return toast.error("Pick at least one insurance type");
    setSaving(true);
    try {
      await onSave({
        name: draft.name.trim(),
        emails: draft.emails,
        insurance_types: draft.insurance_types,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-line rounded-sm p-5 md:p-6 space-y-5" data-testid={`team-card-${team.id || "new"}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1">
          <Label className="text-forest">Team name</Label>
          <Input
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            placeholder="e.g. Northeast Health Team"
            className="mt-1.5 h-10 border-line input-clean"
            data-testid={`team-name-input-${team.id || "new"}`}
          />
        </div>
        {team.id && (
          <Button
            variant="outline"
            onClick={() => onDelete(team.id)}
            className="border-danger/40 text-danger hover:bg-danger hover:text-bone rounded-sm h-10 mt-6"
            data-testid={`team-delete-${team.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>

      <div>
        <Label className="text-forest">Notification recipients</Label>
        <p className="text-xs text-sage mt-1 mb-2">
          When a new application matching this team's insurance types is submitted, these addresses get the email.
        </p>
        <div className="flex gap-2">
          <Input
            value={draft._emailInput}
            onChange={(e) => setDraft({ ...draft, _emailInput: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addEmail();
              }
            }}
            placeholder="agent@example.com"
            className="h-10 border-line input-clean flex-1"
            data-testid={`team-email-input-${team.id || "new"}`}
          />
          <Button
            type="button"
            onClick={addEmail}
            variant="outline"
            className="border-forest text-forest hover:bg-forest hover:text-bone rounded-sm h-10"
            data-testid={`team-email-add-${team.id || "new"}`}
          >
            Add
          </Button>
        </div>
        {draft.emails.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3" data-testid={`team-email-pills-${team.id || "new"}`}>
            {draft.emails.map((e) => (
              <span
                key={e}
                className="inline-flex items-center gap-1.5 bg-cream border border-line rounded-sm px-2.5 py-1 text-xs text-forest"
              >
                {e}
                <button
                  onClick={() => removeEmail(e)}
                  className="text-sage hover:text-danger"
                  data-testid={`team-email-remove-${team.id || "new"}-${e}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div>
        <Label className="text-forest">Insurance types this team handles</Label>
        <div className="flex gap-6 mt-3">
          {[
            { key: "health", label: "Health" },
            { key: "life", label: "Life" },
          ].map((t) => (
            <div key={t.key} className="flex items-center gap-2">
              <Switch
                checked={draft.insurance_types.includes(t.key)}
                onCheckedChange={() => toggleType(t.key)}
                data-testid={`team-type-${team.id || "new"}-${t.key}`}
              />
              <span className="text-sm text-forest">{t.label}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="pt-3 border-t border-line">
        <Button
          onClick={save}
          disabled={saving}
          className="bg-forest hover:bg-forestdark text-bone rounded-sm h-10 px-5"
          data-testid={`team-save-${team.id || "new"}`}
        >
          <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : team.id ? "Save changes" : "Create team"}
        </Button>
      </div>
    </div>
  );
}

export default function TeamsSettings() {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/teams");
      setTeams(data || []);
    } catch {
      toast.error("Failed to load teams");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async (payload) => {
    try {
      await api.post("/teams", payload);
      toast.success("Team created");
      setAdding(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create team");
    }
  };

  const update = (id) => async (payload) => {
    try {
      await api.patch(`/teams/${id}`, payload);
      toast.success("Team updated");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update team");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this team? Submissions will rotate among the remaining teams.")) return;
    try {
      await api.delete(`/teams/${id}`);
      toast.success("Team deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <div data-testid="teams-settings">
      <div className="mb-6">
        <h2 className="font-heading text-2xl tracking-tight font-medium flex items-center gap-2">
          <Users className="w-5 h-5 text-clay" /> Teams & routing
        </h2>
        <p className="text-sage mt-2 text-sm">
          Group your agents into teams. Each new submission is routed to <span className="font-medium text-forest">one team in round-robin order</span>,
          filtered by insurance type. Teams are created in the order shown — that's also the rotation order.
        </p>
      </div>

      {loading ? (
        <div className="text-sage text-sm">Loading teams…</div>
      ) : (
        <div className="space-y-4">
          {teams.length === 0 && !adding && (
            <div className="bg-cream border border-line rounded-sm p-6 text-center text-sage text-sm" data-testid="teams-empty">
              No teams yet. New submissions still go to all admin agents until you set up routing.
            </div>
          )}

          {teams.map((t) => (
            <TeamCard key={t.id} team={t} onSave={update(t.id)} onDelete={remove} />
          ))}

          {adding && <TeamCard team={empty} onSave={create} onDelete={() => setAdding(false)} />}

          {!adding && (
            <Button
              onClick={() => setAdding(true)}
              variant="outline"
              className="w-full border-dashed border-line text-sage hover:text-forest hover:border-forest rounded-sm h-12"
              data-testid="teams-add-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Add team
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
